import { Injectable } from '@angular/core';
import { 
  Request, 
  XHRBackend, 
  RequestOptions, 
  Response, 
  Http, 
  RequestOptionsArgs, 
  Headers 
} from '@angular/http';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/catch';
import 'rxjs/add/observable/throw';

/**
 * HTTP Interceptor that automatically adds x-vmw-esp-client header to all requests
 * This interceptor extends the default XHRBackend to inject the authorization token
 * into all outgoing HTTP requests
 */
@Injectable()
export class HttpInterceptorService extends Http {

  constructor(
    backend: XHRBackend, 
    defaultOptions: RequestOptions
  ) {
    super(backend, defaultOptions);
  }

  /**
   * Performs a request with the provided options, automatically adding the token header
   * @param url URL for the request
   * @param options Optional request options
   * @returns Observable<Response>
   */
  request(url: string | Request, options?: RequestOptionsArgs): Observable<Response> {
    // Handle both string URLs and Request objects
    if (typeof url === 'string') {
      return super.request(url, this.addTokenHeader(options));
    } else {
      // For Request objects, add the header directly to the request
      const token = localStorage.getItem('token');
      if (token) {
        url.headers.set('x-vmw-esp-client', `Bearer ${token}`);
      }
      return super.request(url, options);
    }
  }

  /**
   * Performs a GET request with the token header
   */
  get(url: string, options?: RequestOptionsArgs): Observable<Response> {
    return super.get(url, this.addTokenHeader(options));
  }

  /**
   * Performs a POST request with the token header
   */
  post(url: string, body: any, options?: RequestOptionsArgs): Observable<Response> {
    return super.post(url, body, this.addTokenHeader(options));
  }

  /**
   * Performs a PUT request with the token header
   */
  put(url: string, body: any, options?: RequestOptionsArgs): Observable<Response> {
    return super.put(url, body, this.addTokenHeader(options));
  }

  /**
   * Performs a DELETE request with the token header
   */
  delete(url: string, options?: RequestOptionsArgs): Observable<Response> {
    return super.delete(url, this.addTokenHeader(options));
  }

  /**
   * Performs a PATCH request with the token header
   */
  patch(url: string, body: any, options?: RequestOptionsArgs): Observable<Response> {
    return super.patch(url, body, this.addTokenHeader(options));
  }

  /**
   * Performs a HEAD request with the token header
   */
  head(url: string, options?: RequestOptionsArgs): Observable<Response> {
    return super.head(url, this.addTokenHeader(options));
  }

  /**
   * Performs an OPTIONS request with the token header
   */
  options(url: string, options?: RequestOptionsArgs): Observable<Response> {
    return super.options(url, this.addTokenHeader(options));
  }

  /**
   * Adds the x-vmw-esp-client header with Bearer token to the request options
   * @param options Existing request options
   * @returns Updated request options with the token header
   */
  private addTokenHeader(options?: RequestOptionsArgs): RequestOptionsArgs {
    if (!options) {
      options = new RequestOptions();
    }
    if (!options.headers) {
      options.headers = new Headers();
    }

    const token = localStorage.getItem('token');
    if (token) {
      // Only add header if not already present (avoid duplicates)
      if (!options.headers.has('x-vmw-esp-client')) {
        options.headers.set('x-vmw-esp-client', `Bearer ${token}`);
      }
    }

    // Preserve existing withCredentials setting
    // Don't override if explicitly set to false
    return options;
  }
}

/**
 * Factory function to create the HTTP Interceptor Service
 * @param xhrBackend XHR Backend
 * @param requestOptions Default request options
 * @returns HttpInterceptorService instance
 */
export function httpInterceptorFactory(
  xhrBackend: XHRBackend,
  requestOptions: RequestOptions
): Http {
  return new HttpInterceptorService(xhrBackend, requestOptions);
}



// WEBPACK FOOTER //
// ./src/app/services/http-interceptor.service.ts